import React, { useState, useEffect, useRef } from "react";
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../services/api';
import CaseDashboard from './CaseDashboard';
import Documents from './Documents';
import CaseForms from './CaseForms';
import IntakeForms from './IntakeForms';
import Audit from './Audit';
import InitialConsultation from './InitialConsultation';
import Messages from './Messages';
import Clients from './Clients';
import Settings from './Settings';
import LegalAssistant from './LegalAssistant';
import { IconType } from 'react-icons';

import {
  FiEdit3,
  FiGrid,
  FiUsers,
  FiFolder,
  FiFileText,
  FiMessageCircle,
  FiCalendar,
  FiSettings,
  FiUser,
  FiMenu,
  FiX
} from 'react-icons/fi';
import LawyerInfoTab from "./LawyerInfoTab";

type NavigationItem = 'dashboard' | 'clients'| 'audit' | 'documents' | 'caseForms' | 'intakeForms' |'consultationForms' | 'consultationSummaries' | 'messages' | 'appointments' | 'settings' | 'Lawyer_Profile';

export default function Dashboard() {
  const { user, signOut } = useAuth();
  const [activeSection, setActiveSection] =
    useState<NavigationItem>("consultationForms");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState<string | null>(null);
  const [isProfileComplete, setIsProfileComplete] = useState<boolean | null>(null);
  const [isCheckingProfile, setIsCheckingProfile] = useState(true);

  // New: Interface mode and assistant state
  const [interfaceMode, setInterfaceMode] = useState<
    "traditional" | "ai-first"
  >("traditional");
  const [assistantCollapsed, setAssistantCollapsed] = useState<boolean>(false);
  const assistantRef = useRef<HTMLDivElement>(null);

  const [rightPanelWidth, setRightPanelWidth] = useState(300); // default 300px
  const [isResizing, setIsResizing] = useState(false);
  
  // Feature flags
  const enable_audit = process.env.REACT_APP_AUDIT_FEATURE_FLAG === 'true';
  const showCaseManagement = process.env.REACT_APP_SHOW_CASE_MANAGEMENT !== 'false'; // Default to true
  const showCommunication = process.env.REACT_APP_SHOW_COMMUNICATION !== 'false'; // Default to true
  
  console.log('auditing feature - ', enable_audit);
  console.log('auditing raw - ', String(process.env.REACT_APP_AUDIT_FEATURE_FLAG));

  // Check profile completeness
  const checkProfileCompleteness = async () => {
    try {
      setIsCheckingProfile(true);
      const response = await apiClient.checkProfileComplete();
      console.log('Profile completeness check response:', response);
      const isComplete = response.isComplete === true;
      console.log('Profile is complete:', isComplete);
      setIsProfileComplete(isComplete);
      
      // If profile is incomplete and not already on profile page, redirect
      if (!isComplete && activeSection !== 'Lawyer_Profile') {
        console.log('Redirecting to profile page - profile is incomplete');
        setActiveSection('Lawyer_Profile');
        window.location.hash = 'Lawyer_Profile';
      } else if (isComplete) {
        console.log('Profile is complete - unlocking dashboard');
      }
    } catch (error) {
      console.error('Error checking profile completeness:', error);
      // On error, assume incomplete to be safe
      setIsProfileComplete(false);
    } finally {
      setIsCheckingProfile(false);
    }
  };

  // Fetch subscription plan
  const fetchSubscription = async () => {
    try {
      const response = await apiClient.get('/subscriptions/current');
      if (response.subscription?.plan) {
        setSubscriptionPlan(response.subscription.plan.display_name);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  useEffect(() => {
    if (user?.id) {
      checkProfileCompleteness();
      fetchSubscription();
    }
  }, [user?.id]);

  // Re-check profile when navigating to profile page or when profile section changes
  useEffect(() => {
    if (activeSection === 'Lawyer_Profile' && user?.id) {
      // Re-check profile when user visits profile page
      // Add a small delay to ensure backend has processed the update
      const timer = setTimeout(() => {
        checkProfileCompleteness();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [activeSection]);

  // Also re-check profile completeness periodically when on profile page and incomplete
  useEffect(() => {
    if (activeSection === 'Lawyer_Profile' && user?.id && isProfileComplete !== true) {
      // Poll for profile completion every 2 seconds when profile is incomplete
      const interval = setInterval(() => {
        console.log('Polling for profile completion...');
        checkProfileCompleteness();
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [activeSection, isProfileComplete, user?.id]);

  // Handle subscription change from Settings
  const handleSubscriptionChange = () => {
    fetchSubscription();
  };
  const handleMouseDown = () => {
    setIsResizing(true);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isResizing) {
      // Resizing disabled for overlay mode
      return;
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  // Click outside to minimize AI assistant
  // useEffect(() => {
  //   const handleClickOutside = (event: MouseEvent) => {
  //     if (
  //       !assistantCollapsed &&
  //       assistantRef.current &&
  //       !assistantRef.current.contains(event.target as Node)
  //     ) {
  //       setAssistantCollapsed(true);
  //     }
  //   };

  //   document.addEventListener('mousedown', handleClickOutside);
  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside);
  //   };
  // }, [assistantCollapsed]);

  // Handle hash-based navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1) as NavigationItem;
      
      // Always allow access to profile page
      if (hash === 'Lawyer_Profile') {
        setActiveSection('Lawyer_Profile');
        // Re-check profile when navigating to profile page
        if (user?.id) {
          checkProfileCompleteness();
        }
        return;
      }
      
      // If profile is incomplete, block all other sections
      if (isProfileComplete !== true) {
        console.log('Blocking navigation - profile is incomplete or checking');
        setActiveSection('Lawyer_Profile');
        window.location.hash = 'Lawyer_Profile';
        return;
      }
      
      // Define available sections based on feature flags
      const availableSections: NavigationItem[] = [
        'consultationForms', 
        'consultationSummaries',
        'settings', 
        'Lawyer_Profile'
      ];
      
      // Add case management sections only if enabled
      if (showCaseManagement) {
        availableSections.push('dashboard', 'clients', 'documents', 'caseForms', 'intakeForms');
        if (enable_audit) {
          availableSections.push('audit');
        }
      }
      
      // Add communication sections only if enabled
      if (showCommunication) {
        availableSections.push('messages');
      }
      
      if (hash && availableSections.includes(hash as NavigationItem)) {
        setActiveSection(hash as NavigationItem);
      } else if (!hash) {
        // If no hash, default to consultationForms (always available) if profile is complete
        // Otherwise, default to profile page
        if (isProfileComplete !== true) {
          setActiveSection('Lawyer_Profile');
          window.location.hash = 'Lawyer_Profile';
        } else {
          setActiveSection('consultationForms');
          window.location.hash = 'consultationForms';
        }
      }
    };

    // Only handle hash changes if profile check is complete or we're on profile page
    if (isProfileComplete !== null || activeSection === 'Lawyer_Profile') {
      handleHashChange();
    }
    
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [showCaseManagement, showCommunication, enable_audit, isProfileComplete, activeSection, user?.id]);

    const navigationItems: {
    section: string;
    items: { key: string; label: string; icon: any }[];
  }[] = [
    {
      section: "INITIAL CONSULTATION",
      items: [
        {
          key: "consultationForms",
          label: "Consultation Forms",
          icon: () => React.createElement(FiFileText as any),
        },
        {
          key: "consultationSummaries",
          label: "Consultation Summaries",
          icon: () => React.createElement(FiUsers as any),
        },
      ],
    },

    ...(showCaseManagement ? [{
      section: "CASE MANAGEMENT",
      items: [
        { key: 'intakeForms', label: 'Intake Forms', icon: () => React.createElement(FiEdit3 as any) },
        { key: 'dashboard', label: 'All Cases', icon: () => React.createElement(FiGrid as any) },
        { key: 'clients', label: 'Client Summary', icon: () => React.createElement(FiUsers as any) },
        { key: 'documents', label: 'Case Documents', icon: () => React.createElement(FiFolder as any) },
        ...(enable_audit ? [{ key: 'audit', label: 'Audit', icon: () => React.createElement(FiFileText as any) }] : []),
        { key: 'caseForms', label: 'Case Forms', icon: () => React.createElement(FiFileText as any) },
      ]
    }] : []),
    ...(showCommunication ? [{
      section: "COMMUNICATION",
      items: [{ key: "messages", label: "Messages", icon: FiMessageCircle }],
    }] : []),
    {
      section: "ADMINISTRATION",
      items: [
        { key: 'settings', label: 'Settings', icon: FiSettings },
        { key: 'Lawyer_Profile', label: 'Profile', icon: FiUser },
      ]
    }
  ];

  const renderContent = () => {
    // Always allow access to profile page
    if (activeSection === 'Lawyer_Profile') {
      return <LawyerInfoTab onProfileSaved={checkProfileCompleteness} />;
    }

    // Block all other sections if profile is incomplete
    if (isProfileComplete !== true) {
      return (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%', 
          padding: '2rem',
          textAlign: 'center'
        }}>
          <div style={{
            backgroundColor: '#fff3cd',
            border: '2px solid #ffc107',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '600px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '1rem' }}>⚠️</div>
            <h2 style={{ color: '#856404', marginBottom: '1rem', fontSize: '24px' }}>
              Profile Setup Required
            </h2>
            <p style={{ color: '#856404', marginBottom: '1.5rem', fontSize: '16px', lineHeight: '1.6' }}>
              Please complete your profile information before accessing the dashboard. 
              Your email address is required to receive consultation summary notifications via email.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  setActiveSection('Lawyer_Profile');
                  window.location.hash = 'Lawyer_Profile';
                }}
                style={{
                  backgroundColor: '#ffc107',
                  color: '#856404',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#ffb300'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ffc107'}
              >
                Complete Profile
              </button>
              <button
                onClick={() => {
                  console.log('Manual refresh triggered');
                  checkProfileCompleteness();
                }}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#5a6268'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
              >
                Refresh Status
              </button>
            </div>
            {isCheckingProfile && (
              <p style={{ color: '#856404', marginTop: '1rem', fontSize: '14px' }}>
                Checking profile status...
              </p>
            )}
          </div>
        </div>
      );
    }

    switch (activeSection) {
      case "dashboard":
        return showCaseManagement ? <CaseDashboard /> : <InitialConsultation key="forms" initialTab="forms" />;
      case "clients":
        return showCaseManagement ? <Clients /> : <InitialConsultation key="forms" initialTab="forms" />;
      case 'audit':
        return (showCaseManagement && enable_audit) ? <Audit /> : <InitialConsultation key="forms" initialTab="forms" />;
      case 'documents':
        return showCaseManagement ? <Documents /> : <InitialConsultation key="forms" initialTab="forms" />;
      case "caseForms":
        return showCaseManagement ? <CaseForms /> : <InitialConsultation key="forms" initialTab="forms" />;
      case "intakeForms":
        return showCaseManagement ? <IntakeForms /> : <InitialConsultation key="forms" initialTab="forms" />;
     case "consultationForms":
      return <InitialConsultation key="consultation" initialTab="forms" />;
      case "consultationSummaries":
        return <InitialConsultation key="consultation" initialTab="summaries" />;
      case "messages":
        return showCommunication ? <Messages /> : <InitialConsultation key="forms" initialTab="forms" />;
      case "settings":
        return <Settings onSubscriptionChange={handleSubscriptionChange} />;
      default:
        return <InitialConsultation key="forms" initialTab="forms" />;
    }
  };

  const renderTraditionalLayout = () => (
    <>
      {/* Left Sidebar */}
      <div className={`sidebar ${sidebarCollapsed ? "collapsed" : ""}`}>
        {/* Sidebar Toggle Button */}
        <button
          className="sidebar-toggle"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          title={sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          style={{
            left: sidebarCollapsed ? "40px" : "220px",
          }}
        >
          {sidebarCollapsed ? "→" : "←"}
        </button>

        {!sidebarCollapsed && (
          <div style={{ flex: 1, overflowY: "auto", paddingBottom: "40px" }}>
            {navigationItems.map((section) => (
              <div key={section.section} className="nav-section">
                <h3 className="section-title">{section.section}</h3>
                {section.items.map((item) => {
                  // Allow profile access even if profile is incomplete
                  const isProfileItem = item.key === 'Lawyer_Profile';
                  const isBlocked = !isProfileItem && isProfileComplete !== true;
                  
                  return (
                    <button
                      key={item.key}
                      className={`nav-item ${
                        activeSection === item.key ? "active" : ""
                      } ${isBlocked ? "disabled" : ""}`}
                      onClick={() => {
                        if (isBlocked) {
                          // Redirect to profile if trying to access blocked section
                          setActiveSection('Lawyer_Profile');
                          window.location.hash = 'Lawyer_Profile';
                        } else {
                          setActiveSection(item.key as NavigationItem);
                          window.location.hash = item.key;
                        }
                      }}
                      title={isBlocked ? "Complete your profile first" : item.label}
                      style={isBlocked ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                    >
                      <span className="nav-icon">{item.icon()}</span>
                      <span className="nav-label">{item.label}</span>
                      {isBlocked && <span className="nav-blocked-indicator" title="Complete profile to access">🔒</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* Collapsed Navigation - Show only icons */}
        {sidebarCollapsed && (
          <div className="collapsed-nav">
            {navigationItems.map((section) => (
              <div key={section.section} className="collapsed-nav-section">
                {section.items.map((item) => {
                  const isProfileItem = item.key === 'Lawyer_Profile';
                  const isBlocked = !isProfileItem && isProfileComplete !== true;
                  
                  return (
                    <button
                      key={item.key}
                      className={`collapsed-nav-item ${
                        activeSection === item.key ? "active" : ""
                      } ${isBlocked ? "disabled" : ""}`}
                      onClick={() => {
                        if (isBlocked) {
                          setActiveSection('Lawyer_Profile');
                          window.location.hash = 'Lawyer_Profile';
                        } else {
                          setActiveSection(item.key as NavigationItem);
                          window.location.hash = item.key;
                        }
                      }}
                      title={isBlocked ? "Complete your profile first" : item.label}
                      style={isBlocked ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                    >
                      <span className="nav-icon">{item.icon()}</span>
                      {isBlocked && <span style={{ fontSize: '10px', marginLeft: '2px' }}>🔒</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Central Content */}
      <div
        className={`content-area ${
          sidebarCollapsed ? "sidebar-collapsed" : ""
        }`}
      >
        {renderContent()}
      </div>

      {/* AI Assistant Overlay - Only show if case management is enabled */}
      {showCaseManagement && (
        <>
          {assistantCollapsed ? (
            <button
              className="assistant-expand-btn"
              onClick={() => setAssistantCollapsed(false)}
              title="Expand Assistant"
            >
              {React.createElement(FiMessageCircle as any)}
            </button>
          ) : (
            <div className="assistant-sidebar" ref={assistantRef}>
              <LegalAssistant
                isFullScreen={false}
                onBackToDashboard={() => {
                  setInterfaceMode("traditional");
                  setSidebarCollapsed(false);
                }}
                onCollapse={() => setAssistantCollapsed(true)}
              />
            </div>
          )}
        </>
      )}
    </>
  );

  const renderAIFirstLayout = () => (
    <div className="main-content ai-first">
      {showCaseManagement ? (
        <div className="assistant-fullwidth">
          <LegalAssistant
            isFullScreen={true}
            onBackToDashboard={() => {
              setInterfaceMode("traditional");
              setSidebarCollapsed(false);
            }}
          />
        </div>
      ) : (
        <div className="no-assistant-message">
          <div className="no-assistant-content">
            <h2>Case Management Disabled</h2>
            <p>AI Assistant is not available when case management is disabled.</p>
            <p>You can still use the floating chat button in consultation summaries for AI assistance.</p>
          </div>
        </div>
      )}
    </div>
  );

  // Show loading state while checking profile
  if (isCheckingProfile && isProfileComplete === null) {
    return (
      <div className="dashboard-container">
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div className="loading-spinner" style={{ width: '40px', height: '40px' }}></div>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Checking profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Top Bar */}
      <div className="top-bar">
        <div className="top-bar-left">
          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? React.createElement(FiX as any) : React.createElement(FiMenu as any)}
          </button>
          <h1 className="logo">CaseKaizen</h1>
          {/* Dock/Undock Assistant toggle - Always visible */}
          <div style={{ marginLeft: 12 }}>
            {false && (
              <button
                className="dock-toggle-btn"
                onClick={() => {
                  if (interfaceMode === "ai-first") {
                    setInterfaceMode("traditional");
                    setSidebarCollapsed(false);
                  } else {
                    setInterfaceMode("ai-first");
                    setSidebarCollapsed(true);
                  }
                }}
                title={
                  interfaceMode === "ai-first" ? "Dashboard" : "Open Assistant"
                }
              >
                {interfaceMode === "ai-first"
                  ? "📊 Dashboard"
                  : "🤖 Open Assistant"}
              </button>
            )}
          </div>
        </div>
        <div className="top-bar-right">
          {subscriptionPlan && (
            <div className="subscription-badge" title="Current subscription plan">
              <span className="badge-text">{subscriptionPlan}</span>
            </div>
          )}
          <div className="user-profile">
            <span className="user-avatar">
              {React.createElement(FiUser as any)}
            </span>
            <span className="user-name">
              {user?.user_metadata?.full_name ||
                user?.user_metadata?.name ||
                user?.user_metadata?.username ||
                user?.email?.split("@")[0] ||
                "User"}
            </span>
            <span className="dropdown-arrow">▼</span>
            <div className="user-dropdown">
              <button onClick={signOut}>Sign Out</button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-menu-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <h2>Menu</h2>
              <button
                className="mobile-menu-close"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                {React.createElement(FiX as any)}
              </button>
            </div>
            <div className="mobile-menu-content">
              {navigationItems.map((section) => (
                <div key={section.section} className="mobile-nav-section">
                  <h3 className="mobile-section-title">{section.section}</h3>
                  {section.items.map((item) => {
                    const isProfileItem = item.key === 'Lawyer_Profile';
                    const isBlocked = !isProfileItem && isProfileComplete !== true;
                    
                    return (
                      <button
                        key={item.key}
                        className={`mobile-nav-item ${
                          activeSection === item.key ? "active" : ""
                        } ${isBlocked ? "disabled" : ""}`}
                        onClick={() => {
                          if (isBlocked) {
                            setActiveSection('Lawyer_Profile');
                            window.location.hash = 'Lawyer_Profile';
                          } else {
                            setActiveSection(item.key as NavigationItem);
                            window.location.hash = item.key;
                          }
                          setMobileMenuOpen(false);
                        }}
                        title={isBlocked ? "Complete your profile first" : item.label}
                        style={isBlocked ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                      >
                        <span className="mobile-nav-icon">{item.icon()}</span>
                        <span className="mobile-nav-label">{item.label}</span>
                        {isBlocked && <span className="mobile-nav-blocked" title="Complete profile to access">🔒</span>}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="main-content">
        {interfaceMode === "traditional"
          ? renderTraditionalLayout()
          : renderAIFirstLayout()}
      </div>
    </div>
  );
}
